import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';

export interface UserSubscription {
  id: number;
  user_id: number;
  package_code: string;
  package_name: string | null;
  billing_cycle: string | null;
  subscription_order: number;
  start_date: string;
  end_date: string | null;
  is_active: boolean;
  auto_renew: boolean;
  created_at: string;
  updated_at: string;
  pending_package_code: string | null;
  pending_billing_cycle: string | null;
  pending_effective_date: string | null;
  stripe_subscription_schedule_id: string | null;
  billing_status: string;
  stripe_subscription_id: string | null;
  stripe_status: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean | null;
  discount_usage_count: number;
  customer_email: string | null;
  customer_first_name: string | null;
  customer_last_name: string | null;
}

interface UserSubscriptionsResponse {
  subscriptions: UserSubscription[];
  pagination: { page: number; limit: number; total: number; pages: number };
  stats: { total: number; active: number; inactive: number };
}

// Matches the derived billing_status shown in the Status column (server-side
// canonical values), so filtering lines up with the badge the user sees.
export type SubscriptionStatus = 'Active' | 'Past Due' | 'Expired' | 'Cancelled' | 'Trialing' | 'Unpaid' | 'Promotional';

export interface UserSubscriptionsFilters {
  userId?: string;
  status?: SubscriptionStatus;
  packageCode?: string;
  search?: string;
  includeInactive?: boolean;
  page?: number;
  limit?: number;
}

function toQueryString(filters: UserSubscriptionsFilters): string {
  const params = new URLSearchParams();
  if (filters.userId) params.set('userId', filters.userId);
  if (filters.status) params.set('status', filters.status);
  if (filters.packageCode) params.set('packageCode', filters.packageCode);
  if (filters.search) params.set('search', filters.search);
  if (filters.includeInactive) params.set('includeInactive', 'true');
  if (filters.page) params.set('page', String(filters.page));
  if (filters.limit) params.set('limit', String(filters.limit));
  return params.toString();
}

/**
 * The CSV export of every subscription matching `filters` (the page's own
 * filters, minus pagination: the file holds all matches, not one page).
 */
export function userSubscriptionsExportPath(filters: Omit<UserSubscriptionsFilters, 'page' | 'limit'>): string {
  const query = toQueryString({ ...filters, page: undefined, limit: undefined });
  return query ? `/user-subscriptions/export?${query}` : '/user-subscriptions/export';
}

export function useUserSubscriptions(filters: UserSubscriptionsFilters) {
  return useQuery({
    queryKey: ['user-subscriptions', filters],
    queryFn: () => apiClient<UserSubscriptionsResponse>(`/user-subscriptions?${toQueryString(filters)}`),
  });
}

/**
 * Every subscription one user has had, current and previous, newest first. The
 * most any user has is a dozen or so, so one page of 100 holds them all.
 */
export function useSubscriptionsForUser(userId: string) {
  return useUserSubscriptions({ userId, includeInactive: true, limit: 100 });
}

function useInvalidateUserSubscriptions() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['user-subscriptions'] });
}

export interface CreateSubscriptionInput {
  user_id: number;
  package_code: string;
  start_date: string;
  billing_cycle?: string;
  end_date?: string;
  auto_renew?: boolean;
}

export function useCreateUserSubscription() {
  const invalidate = useInvalidateUserSubscriptions();
  return useMutation({
    mutationFn: (input: CreateSubscriptionInput) =>
      apiClient<UserSubscription>('/user-subscriptions', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: invalidate,
  });
}

// Direct edit of an existing subscription's own fields (PUT /user-subscriptions/:id).
// Distinct from "change plan" (a Stripe-scheduled swap at renewal) and "cancel" -
// this is the manual/comp-fix edit the old admin had. Only fields present are
// updated server-side (buildUpdateSubscriptionSql), so send only what changed.
export interface UpdateSubscriptionInput {
  package_code?: string;
  start_date?: string;
  end_date?: string | null;
  is_active?: boolean;
  auto_renew?: boolean;
}

export function useUpdateUserSubscription(id: number) {
  const invalidate = useInvalidateUserSubscriptions();
  return useMutation({
    mutationFn: (input: UpdateSubscriptionInput) =>
      apiClient<UserSubscription>(`/user-subscriptions/${id}`, { method: 'PUT', body: JSON.stringify(input) }),
    onSuccess: invalidate,
  });
}

interface CancelResponse {
  message: string;
  cancellationType: 'instant' | 'period-end';
  stripeOperation: { action: string; effectiveDate: string | null } | null;
}

export function useCancelUserSubscription() {
  const invalidate = useInvalidateUserSubscriptions();
  return useMutation({
    mutationFn: ({ id, type }: { id: number; type: 'instant' | 'period-end' }) =>
      apiClient<CancelResponse>(`/user-subscriptions/${id}?type=${type}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

interface SchedulePlanChangeResponse {
  success: boolean;
  scheduleId: string;
  effectiveDate: string;
  newPackageCode: string;
  newBillingCycle: string;
}

export function useSchedulePlanChange(subscriptionId: number) {
  const invalidate = useInvalidateUserSubscriptions();
  return useMutation({
    mutationFn: (input: { newPackageCode: string; newBillingCycle: 'weekly' | 'monthly' }) =>
      apiClient<SchedulePlanChangeResponse>(`/user-subscriptions/${subscriptionId}/schedule-change`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: invalidate,
  });
}

export function useReleasePlanChange(subscriptionId: number) {
  const invalidate = useInvalidateUserSubscriptions();
  return useMutation({
    mutationFn: () => apiClient<{ success: boolean }>(`/user-subscriptions/${subscriptionId}/schedule-change`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}
